import { Fragment, useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type { ClassState } from "../../shared/api/types";
import { Button } from "../../shared/ui/Button";
import { ConfirmDialog } from "../../shared/ui/Modal";
import { usePageExitGuard } from "../../shared/ui/PageExitGuard";
import { useToast, errorMessage } from "../../shared/ui/ToastProvider";
import { canWrite, useApplyStudentPoints } from "./actions";
import { moveStudent, resizeSeating, parseSeatingLayout } from "./seating";
import type { SeatingLayout } from "./seating";
import "./seating-rewards.css";

export function SeatingChart({ state }: { state: ClassState }) {
  const toast = useToast();
  const writable = canWrite(state);
  const queryClient = useQueryClient();
  const route = `/api/classes/${state.classId}/seating`;
  const queryKey = ["seating", state.organizationId, state.classId];
  const query = useQuery({
    queryKey,
    queryFn: async () => parseSeatingLayout((await api<SeatingLayout>(route)).data, state.classId!),
    // A deleted/restored student must not reappear in a cached seat on return.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true
  });
  const [draft, setDraft] = useState<SeatingLayout | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAisles, setShowAisles] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<number | null>(null);
  const [pending, setPending] = useState<{ layout: SeatingLayout; message: string; rosterKey: string } | null>(null);
  const [dragging, setDragging] = useState<{ id: number; x: number; y: number } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const pointer = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const applyPoints = useApplyStudentPoints();
  const layout = editing ? draft : query.data;
  const rosterKey = state.students.map((student) => student.id).sort((a, b) => a - b).join(",");
  const previousRoster = useRef(rosterKey);
  useEffect(() => {
    if (previousRoster.current === rosterKey) return;
    previousRoster.current = rosterKey;
    const ids = new Set(rosterKey.split(",").filter(Boolean).map(Number));
    setDraft((current) => current ? { ...current, seats: Object.fromEntries(Object.entries(current.seats).filter(([, id]) => ids.has(id))) } : current);
    setSelected((id) => id !== null && ids.has(id) ? id : null);
    // Confirmations contain a layout snapshot; never apply a stale roster later.
    setPending(null);
    void queryClient.invalidateQueries({ queryKey: ["seating", state.organizationId, state.classId] });
  }, [rosterKey, queryClient, state.organizationId, state.classId]);
  usePageExitGuard(editing, busy ? "座位正在保存或载入，请等待完成后再离开。" : "座位调整尚未保存，确定放弃并离开？", busy);
  const place = (id: number, key: string) => {
    if (layout && editing && !busy && writable && state.students.some((s) => s.id === id)) { setDraft(moveStudent(layout, id, key)); setSelected(null); }
  };
  const start = (event: PointerEvent<HTMLButtonElement>, id: number) => {
    if (!editing || busy || event.button !== 0) return;
    pointer.current = { id, x: event.clientX, y: event.clientY, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const move = (event: PointerEvent<HTMLButtonElement>) => {
    const p = pointer.current;
    if (!p) return;
    if (Math.hypot(event.clientX - p.x, event.clientY - p.y) > 7) p.moved = true;
    if (p.moved) {
      setDragging({ id: p.id, x: event.clientX, y: event.clientY });
      const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-seat-target]");
      setDropTarget(target?.dataset.seatTarget ?? null);
    }
  };
  const finish = (event: PointerEvent<HTMLButtonElement>) => {
    const p = pointer.current;
    pointer.current = null;
    setDragging(null);
    setDropTarget(null);
    if (!p?.moved) return;
    suppressClick.current = true;
    window.setTimeout(() => { suppressClick.current = false; }, 0);
    const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-seat-target]");
    if (target?.dataset.seatTarget) place(p.id, target.dataset.seatTarget);
  };
  const save = async () => {
    if (!draft || busy) return;
    setBusy(true);
    try {
      const result = await api<SeatingLayout>(route, { method: "PUT", body: JSON.stringify(draft) });
      const saved = parseSeatingLayout(result.data, state.classId!);
      queryClient.setQueryData(queryKey, saved);
      setEditing(false); setSelected(null);
      toast("座位表已保存", "success");
      await query.refetch();
    } catch (error) { toast(errorMessage(error, "保存失败，草稿已保留"), "error"); }
    finally { setBusy(false); }
  };
  const reload = async () => {
    if (busy || !window.confirm("丢弃当前草稿，载入服务器最新座位表？")) return;
    setBusy(true);
    try {
      const result = await query.refetch();
      if (result.isError || !result.data) {
        toast("重新载入失败，草稿已保留", "error");
      } else {
        setDraft(result.data);
        setSelected(null);
      }
    } finally { setBusy(false); }
  };
  const changeSize = (rows: number, cols: number) => {
    if (!layout || !Number.isInteger(rows) || !Number.isInteger(cols) || rows < 1 || rows > 12 || cols < 1 || cols > 12) return;
    const next = resizeSeating(layout, rows, cols);
    const removed = Object.values(layout.seats).filter((id) => !Object.values(next.seats).includes(id));
    if (removed.length) setPending({ layout: next, rosterKey, message: `以下学生将回到未安排区：${removed.map((id) => state.students.find((s) => s.id === id)?.name).join("、")}` });
    else setDraft(next);
  };
  const autoFill = () => {
    if (!layout) return;
    const next = { ...layout, seats: Object.fromEntries(state.students.slice(0, layout.rows * layout.cols).map((s, i) => [`${Math.floor(i / layout.cols)}-${i % layout.cols}`, s.id])) };
    if (Object.keys(layout.seats).length) setPending({ layout: next, rosterKey, message: "将按当前名单重新安排座位，超出容量的学生保留在未安排区。" });
    else { setDraft(next); toast("已按名单填充座位", "success"); }
  };
  const importOld = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(`classroomSeatLayout:${state.classId}`) || "null");
      if (!raw || !query.data || query.data.version !== 0) throw new Error("没有可导入的旧布局，或服务器已有座位表");
      if (!Number.isInteger(raw.rows) || !Number.isInteger(raw.cols) || raw.rows < 1 || raw.rows > 12 || raw.cols < 1 || raw.cols > 12 || !raw.seats || typeof raw.seats !== "object") throw new Error("旧布局格式不正确");
      const ids = Object.values(raw.seats) as number[];
      if (new Set(ids).size !== ids.length || ids.some((id) => !state.students.some((s) => s.id === id))) throw new Error("旧布局包含不属于当前班级的学生，无法导入");
      const next = resizeSeating({ ...query.data, rows: raw.rows, cols: raw.cols, seats: raw.seats }, raw.rows, raw.cols);
      if (Object.keys(next.seats).length !== ids.length) throw new Error("旧布局包含超出范围的座位");
      setPending({ layout: next, rosterKey, message: `预览：${raw.rows} 行 ${raw.cols} 列，${ids.map((id) => state.students.find((s) => s.id === id)?.name).join("、")}。导入后仍需点击保存。` });
    } catch (error) { toast(errorMessage(error, "旧布局无法读取"), "error"); }
  };
  if (query.isError && !layout) return <article className="surface-panel"><p role="alert">座位表加载失败</p><Button onClick={() => query.refetch()}>重试</Button></article>;
  if (!layout) return <article className="surface-panel">正在读取座位表…</article>;

  const assigned = new Set(Object.values(layout.seats));
  const unassigned = state.students.filter((student) => !assigned.has(student.id));
  const columns = "24px " + Array.from({ length: layout.cols }, (_, c) =>
    "minmax(68px, 1fr)" + (layout.colAisles.includes(c + 1) ? " 30px" : "")).join(" ");
  const minWidth = 24 + layout.cols * 80 + layout.colAisles.length * 38;
  return <div className="seating-shell" data-editing={editing}>
    <article className="surface-panel seating-panel">
      <div className="seating-heading"><h3>班级座位表</h3><span className={editing ? "reward-status warning" : "reward-status"}>{editing ? "编辑中" : "全班共用"}</span></div>
      <div className="seating-toolbar">
        {editing && <div className="panel-actions wrap">
          <select aria-label="座位行数" value={layout.rows} disabled={busy} onChange={(e) => changeSize(Number(e.target.value), layout.cols)}>{Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1}>{i + 1} 行</option>)}</select>
          <select aria-label="座位列数" value={layout.cols} disabled={busy} onChange={(e) => changeSize(layout.rows, Number(e.target.value))}>{Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1}>{i + 1} 列</option>)}</select>
          <Button variant="secondary" disabled={busy} aria-expanded={showAisles} onClick={() => setShowAisles(!showAisles)}>走道设置</Button>
          <Button variant="secondary" disabled={busy} onClick={autoFill}>按名单自动填充</Button>
        </div>}
        <div className="panel-actions wrap seating-save-actions">
          {writable && !editing && <Button onClick={() => { setDraft(query.data!); setEditing(true); }}>编辑座位</Button>}
          {editing && <><Button variant="secondary" disabled={busy} onClick={() => { setEditing(false); setSelected(null); }}>取消编辑</Button><Button onClick={save} disabled={busy}>保存座位</Button></>}
          {writable && !editing && layout.version === 0 && <Button variant="secondary" onClick={importOld}>导入旧浏览器布局</Button>}
        </div>
      </div>
      <p className="seating-hint">{editing ? "拖动人名安排或交换座位，也可先选学生再点座位。编辑时不会记分。" : writable ? "点击学生 +1，Shift + 点击 −1。座位布局全班共用。" : "当前账号为只读，可查看本班座位安排。"}</p>
      {query.isError && <p role="alert">座位读取失败，已保留当前布局和草稿</p>}
      {editing && showAisles && <div className="seating-options">
        {(["rowAisles", "colAisles"] as const).map((key) => <fieldset key={key} disabled={busy}><legend>{key === "rowAisles" ? "横走道（在第几行后）" : "竖走道（在第几列后）"}</legend>{Array.from({ length: (key === "rowAisles" ? layout.rows : layout.cols) - 1 }, (_, i) => i + 1).map((n) => <label key={n}><input type="checkbox" checked={layout[key].includes(n)} onChange={(e) => setDraft({ ...layout, [key]: e.target.checked ? [...layout[key], n] : layout[key].filter((v) => v !== n) })} />{n}</label>)}</fieldset>)}
      </div>}
      <div className="seating-stage">讲台</div>
      <div className="seat-grid-wrap"><div className="seating-board" style={{ minWidth }}>
        <div className="seating-row seating-column-labels" style={{ gridTemplateColumns: columns }}><span />{Array.from({ length: layout.cols }, (_, c) => <Fragment key={c}><span>{c + 1}</span>{layout.colAisles.includes(c + 1) && <span />}</Fragment>)}</div>
        {Array.from({ length: layout.rows }, (_, r) => <div key={r} className="seating-row" style={{ gridTemplateColumns: columns, marginBottom: layout.rowAisles.includes(r + 1) ? 30 : 12 }}>
          <span className="seating-row-label">{r + 1}</span>
          {Array.from({ length: layout.cols }, (_, c) => {
            const key = `${r}-${c}`, student = state.students.find((s) => s.id === layout.seats[key]);
            return <Fragment key={key}>
              <button type="button" data-seat-target={key} aria-label={`座位 ${r + 1}-${c + 1} ${student?.name || "空座"}`} aria-pressed={!!student && selected === student.id}
                className={`seat-cell ${student ? "occupied" : "seat-empty"} ${editing ? "seat-editable" : ""} ${selected === student?.id ? "seat-selected" : ""} ${dropTarget === key ? "seat-drop-target" : ""} ${student && dragging?.id === student.id ? "seat-drag-origin" : ""}`} disabled={busy || !writable || (!editing && !student)}
                onPointerDown={(e) => { if (student) start(e, student.id); }} onPointerMove={move} onPointerUp={finish} onPointerCancel={() => { pointer.current = null; setDragging(null); setDropTarget(null); }}
                onClick={(e) => { if (suppressClick.current) return; if (editing) { if (selected) place(selected, key); else if (student) setSelected(student.id); } else if (student) applyPoints.mutate({ studentIds: [student.id], amount: e.shiftKey ? -1 : 1, reason: "座位表", category: "manual" }); }}>
                <small>{r + 1}-{c + 1}</small><strong>{student?.name || "空座"}</strong>{student && !editing && <span>总分 {student.points}</span>}
              </button>
              {layout.colAisles.includes(c + 1) && <span className="seating-aisle">{r === Math.floor(layout.rows / 2) ? "走道" : ""}</span>}
            </Fragment>;
          })}
        </div>)}
      </div></div>
      {editing && <Button variant="secondary" size="small" disabled={busy} onClick={reload}>重新载入</Button>}
    </article>
    <aside className={`surface-panel seating-unassigned ${dropTarget === "unassigned" ? "seating-drop-active" : ""}`} data-seat-target="unassigned"><h4>未安排学生 · {unassigned.length}</h4>
      <p className="setting-desc">{editing ? "拖入空座即可安排" : writable ? "进入编辑模式安排座位" : "当前账号仅可查看"}</p>
      <input aria-label="搜索未安排学生" placeholder="搜索学生" value={search} onChange={(event) => setSearch(event.target.value)} />
      <div className="seating-name-list">{unassigned.filter((s) => s.name.includes(search.trim())).map((s) => <button type="button" className={`seat-name ${editing ? "seat-editable" : ""}`} key={s.id} disabled={!editing || busy} aria-pressed={selected === s.id}
        onPointerDown={(e) => start(e, s.id)} onPointerMove={move} onPointerUp={finish} onPointerCancel={() => { pointer.current = null; setDragging(null); setDropTarget(null); }} onClick={() => { if (!suppressClick.current) setSelected(selected === s.id ? null : s.id); }}>{s.name}</button>)}</div>
      {!state.students.length && <p>请先添加学生，再安排座位。</p>}
      {unassigned.length === 0 && state.students.length > 0 && <p className="setting-desc">全班学生已安排</p>}
      <button className="seating-drop-zone" data-seat-target="unassigned" disabled={!editing || busy || selected === null} onClick={() => { if (selected) place(selected, "unassigned"); }}>拖到这里，移出座位{selected !== null && <small>也可点击移出所选学生</small>}</button>
      <p className="seating-summary">已安排 <strong>{state.students.length - unassigned.length}</strong> 人 · 共 <strong>{layout.rows * layout.cols}</strong> 个座位</p>
    </aside>
    {dragging && <div className="seating-drag-preview" style={{ left: dragging.x + 12, top: dragging.y + 12 }}>{state.students.find((s) => s.id === dragging.id)?.name}</div>}
    {pending && <ConfirmDialog title="确认调整座位" detail={pending.message} onClose={() => setPending(null)} onConfirm={() => {
      if (pending.rosterKey === rosterKey && writable) { setDraft(pending.layout); setEditing(true); }
      else toast("名单或权限已变化，请重新确认调整", "error");
      setPending(null);
    }} />}
  </div>;
}
